import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n, LANGS } from "../lib/i18n.jsx";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { HardHat, Loader2 } from "lucide-react";
import { getCachedBranding } from "../lib/theme";
import { fileUrl } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1625722776951-39123efa4dc3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function BrandMark({ size = 26, className = "", full = false }) {
  const b = getCachedBranding();
  const logo = b?.logo_file_id ? fileUrl(b.logo_file_id) : b?.logo_url;
  const isDefault = !b || !logo;
  if (full) {
    const src = isDefault ? "/brand/logo-full.png" : logo;
    return <img src={src} alt="logo" data-testid="login-logo" className={`mx-auto w-[220px] sm:w-[300px] md:w-[360px] lg:w-[420px] max-w-[80vw] h-auto object-contain drop-shadow-[0_0_30px_rgba(0,200,230,0.25)] ${className}`} />;
  }
  if (logo || isDefault) return <img src={isDefault ? "/brand/mark.png" : logo} alt="logo" className={`object-contain ${className}`} style={{ height: size + 10, maxWidth: 200 }} data-testid="login-logo" />;
  return <span className={`inline-flex items-center gap-2 text-[var(--amber)] font-display font-black ${className}`}><HardHat size={size} /> {b?.app_name || "KONVIA"}</span>;
}

export default function Login() {
  const { user, login } = useAuth();
  const { lang, setLang, t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success(t("welcome"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (!lang) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--sidebar)] p-6">
        <div className="w-full max-w-md text-center animate-fadeup">
          <div className="mb-6 flex justify-center"><BrandMark full /></div>
          <p className="text-slate-400 mb-8">{t("choose_lang")}</p>
          <div className="grid grid-cols-2 gap-3">
            {LANGS.map((l) => (
              <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)}
                style={{ background: "var(--sidebar-2)" }}
                className="big-touch rounded-xl border border-slate-700 text-white flex items-center justify-center gap-3 text-lg font-semibold hover:border-[var(--amber)] transition-colors">
                <span className="text-2xl">{l.flag}</span> {l.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--sidebar)]">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="site" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--sidebar)] via-transparent to-transparent" />
        <div className="relative p-12 h-full flex flex-col justify-end">
          <div className="mb-3"><BrandMark full className="!mx-0 !w-[280px]" /></div>
          <h1 className="text-white font-display text-3xl font-bold max-w-md leading-tight">
            {t("app_tag")}
          </h1>
          <p className="text-slate-400 mt-3 max-w-md">{t("login_tagline")}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fadeup">
          <div className="flex items-center justify-between mb-6">
            <div className="lg:hidden"><BrandMark size={62} /></div>
            <select data-testid="login-lang-select" value={lang} onChange={(e) => setLang(e.target.value)}
              className="ml-auto bg-[var(--sidebar-2)] text-slate-300 border border-slate-700 rounded-md px-2 py-1 text-sm">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
            </select>
          </div>

          <h2 className="text-white font-display text-2xl font-bold mb-1">{t("login")}</h2>
          <p className="text-slate-500 text-sm mb-6">{t("app_tag")}</p>

          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="login-email" type="email" placeholder={t("email")} value={email}
              onChange={(e) => setEmail(e.target.value)} required className="bg-[var(--sidebar-2)] border-slate-700 text-white h-11" />
            <Input data-testid="login-password" type="password" placeholder={t("password")} value={password}
              onChange={(e) => setPassword(e.target.value)} required className="bg-[var(--sidebar-2)] border-slate-700 text-white h-11" />
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-11 bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white font-bold">
              {busy ? <Loader2 className="animate-spin" size={18} /> : t("login")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
